import {
	AfterViewChecked,
	AfterViewInit,
	Component,
	ElementRef,
	inject,
	InjectionToken,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { DynamicModule } from 'ng-dynamic-component';
import {
	combineLatest,
	filter,
	map,
	Observable,
	of,
	shareReplay,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs';

import { CookieManagerComponent } from '@valk-nx/compositions/ui-cookie-manager/src/lib/cookie-manager.component';
import { TranslatedSlug } from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import {
	HOTEL_SLUG,
	LANGUAGE,
	Language,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { BannerComponent as SbBannerComponent } from '@valk-nx/storyblok/components/banner/src/lib/banner';
import { FooterComponent as SbFooterComponent } from '@valk-nx/storyblok/components/footer/src/lib/footer';
import { HeaderComponent } from '@valk-nx/storyblok/components/header/src/lib/header';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import { PageSettingsStore } from '@valk-nx/storyblok-store/src/lib/store/page-settings/page-settings.store';
import { StoryblokGlobals } from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

const Components = {
	banner: SbBannerComponent,
	footer: SbFooterComponent,
	header: HeaderComponent,
};

export const SHOW_COOKIE_BANNER = new InjectionToken<string>(
	'show-cookie-banner',
);

@Component({
	selector: 'sb-wrapper',
	templateUrl: './wrapper.component.html',
	imports: [DynamicModule, CookieManagerComponent, LetDirective],
	providers: [HotelStore, PageSettingsStore],
})
export class SbWrapperComponent
	implements AfterViewInit, OnDestroy, OnInit, AfterViewChecked
{
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly router = inject(Router);
	private readonly hotelSlug = inject(HOTEL_SLUG);
	private readonly renderer2 = inject(Renderer2);
	readonly showCookieBanner = inject(SHOW_COOKIE_BANNER);
	private readonly language = inject<Language>(LANGUAGE);

	readonly hotelStore = inject(HotelStore);
	readonly pageSettingsStore = inject(PageSettingsStore);
	@ViewChild('headerRef', { static: false }) headerRef!: ElementRef;

	wrapper$!: Observable<{
		header: StoryblokGlobals['header'] & {
			translatedSlugs?: TranslatedSlug[];
		};
		banner: StoryblokGlobals['banner'];
		footer: StoryblokGlobals['footer'];
		language: Language;
	}>;
	components: typeof Components = Components;
	storyblokComponent = '';
	cookieTranslations = {
		nl: {
			extra: {
				url: '/cookies',
			},
		},
		ca: {
			extra: {
				url: '/cookies',
			},
		},
		en: {
			extra: {
				url: '/cookies',
			},
		},
		es: {
			extra: {
				url: '/cookies',
			},
		},
		de: {
			extra: {
				url: '/cookies',
			},
		},
		fr: {
			extra: {
				url: '/cookies',
			},
		},
	};

	isNegativeHeader = false;
	destroy$ = new Subject<void>();

	hideMenu: Observable<boolean>;

	constructor() {
		this.hideMenu = toObservable(this.pageSettingsStore.hideMenu);
	}

	ngOnInit() {
		this.wrapper$ = combineLatest([
			this.sbService.globals$,
			this.sbService.translatedSlugs$,
			this.hideMenu,
		]).pipe(
			takeUntil(this.destroy$),
			map(([sbGlobals, translatedSlugs, hideMenu]) => {
				const wrapper = {
					...sbGlobals,
				};
				wrapper.header['translatedSlugs'] = translatedSlugs;
				wrapper.header.hideMenu = hideMenu;

				wrapper.language = (
					translatedSlugs?.find((slug) => slug.selected)?.lang ??
					this.language
				).toLowerCase() as Language;
				return wrapper;
			}),
			switchMap((wrapper) => {
				if (this.hotelSlug) {
					return this.contentService
						.getHotelInfo(wrapper.language)
						.pipe(
							tap((data) => {
								const hotelData = data.data;
								this.metadataService.setSchemaOrgHotel(
									this.renderer2,
									hotelData,
								);

								this.hotelStore.updateState(hotelData);
							}),
							map(() => wrapper),
							shareReplay(1),
						);
				} else {
					return of(wrapper);
				}
			}),
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.destroy$),
			)
			.subscribe(() => {
				const urlTree = this.router.parseUrl(this.router.url);
				const primarySegment =
					urlTree.root.children['primary']?.segments[0]?.path;

				/* istanbul ignore else */
				if (primarySegment === this.language) {
					// Get the segments after the language segment
					const segments =
						urlTree.root.children['primary']?.segments.slice(1) ??
						[];
					const newPath =
						'/' + segments.map((segment) => segment.path).join('/');

					const navigationExtras: NavigationExtras = {};
					if (Object.keys(urlTree.queryParams).length > 0) {
						navigationExtras.queryParams = urlTree.queryParams;
					}
					if (urlTree.fragment) {
						navigationExtras.fragment = urlTree.fragment;
					}

					this.router.navigate([newPath], navigationExtras);
				}

				this.storyblokComponent = urlTree.queryParams['_storyblok_c'];
			});
	}

	ngAfterViewChecked() {
		negativeHeader$.pipe(take(1)).subscribe((isNegative) => {
			this.isNegativeHeader = isNegative;
			this.checkHeight();
		});
	}

	checkHeight() {
		if (this.isNegativeHeader) {
			this.headerRef?.nativeElement.classList.add('absolute', 'w-full');
		} else {
			this.headerRef?.nativeElement.classList.remove(
				'absolute',
				'w-full',
			);
		}
	}
}
